import React, {
    useEffect,
    useState,
    useRef
} from 'react';
import PropTypes from 'prop-types';

import Panel from '../../../components/Panel.jsx';
import RIQModal from '../../../components/RIQModal.jsx';
import RIQButton from '../../../components/RIQButton.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import MetricTimePeriodHeaderTitle from '../../../components/table/MetricTimePeriodHeaderTitle.jsx';
import DeleteListeningQueryButton from './DeleteListeningQueryButton.jsx';

import {
    useCurationConfigValue,
    useCurationConfigState
} from '../../../hooks/widgetConfiguration.js';

import useMetrics from '../../../hooks/useMetrics.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useIsExporting,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    RIQInfoPopover,
    useRIQTooltip
} from '../../../components/RIQPopover.jsx';

import useValidateSavedSearchName from '../hooks/useValidateSavedSearchName.js';
import useListeningSearchTimePeriod from '../hooks/useListeningSearchTimePeriod.js';
import useViewListeningQuery from '../hooks/useViewListeningQuery.js';

import {
    useListAccountSavedSearches,
    useSaveLandscapeListeningQuery,
    useListLandscapeSavedSearches,
    useListSampleListeningQueries
} from '../hooks/useListeningQueryPersistence.js';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus
} from '../../../hooks/loadingStatusHooks.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

const {
    title,
    description
} = listeningDefinitions['saved-search-list'];

SavedSearchListWidget.propTypes = {
    appSpecificExportConfig: PropTypes.object,
    pdfExportConfig: PropTypes.object,
    instanceId: PropTypes.string.isRequired
};


function RenameTopicButton({savedSearch}) {

    const inputRef = useRef();
    const [newSearchName, setNewSearchName] = useState(savedSearch.name || '');
    const [changedSearchText, setChangedSearchText] = useState((newSearchName !== savedSearch.name));

    const [isModalVisible, setIsModalVisible] = useState(false);

    function onRenameSavedSearch(e) {
        e.stopPropagation();
        setNewSearchName(savedSearch.name || '');
        setIsModalVisible(true);
    }

    const {setTriggerRef: renameTriggerRef, tooltipNode: renameTooltipNode} = useRIQTooltip({
        text: 'Edit Search Name',
        placement: 'top',
        delayShow: 200
    });

    const {mutate: persistToServer} = useSaveLandscapeListeningQuery(savedSearch.id);
    const isInvalidSavedSearch = useValidateSavedSearchName(newSearchName, savedSearch.id);

    function onNewSearchNameChange(e) {
        const name = e.target.value;
        setNewSearchName(name);
        setChangedSearchText((name !== savedSearch.name));
    }

    function onCloseClick() {
        setIsModalVisible(false);
    }

    function onSaveClick() {
        persistToServer(_.defaults({
            name: newSearchName
        }, savedSearch), {
            onSettled: (data, error) => {
                setIsModalVisible(false);
            }
        });
    }

    function onKeyDown(e) {
        if (e.key === 'Enter' && changedSearchText && !isInvalidSavedSearch) {
            onSaveClick();
        }
    }

    return (
        <>
            <button
                className='tlw-border-none tlw-bg-transparent tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal tlw-shadow-none'
                onClick={onRenameSavedSearch}
                ref={renameTriggerRef}
            >
                <span className='fa fa-fw fa-edit tlw-text-md' />
            </button>
            {!isModalVisible && renameTooltipNode}

            <RIQModal
                initialFocus={inputRef}
                onModalDismissed={onCloseClick}
                showModal={isModalVisible}
                title='Edit Search Name'
                variant='small'>
                <div className='tlw-m-10'>
                    <input
                        className='tlw-mr-20 tlw-w-full tlw-text-ellipsis tlw-rounded-sm tlw-border tlw-border-solid tlw-border-gray-600 tlw-py-3 tlw-pl-6 tlw-pr-6 tlw-text-md tlw-leading-20 tlw-shadow-sm tlw-outline-none focus_tlw-border-blue-400 focus_tlw-shadow-inner focus_tlw-outline-none disabled_tlw-pointer-events-none disabled_tlw-cursor-not-allowed disabled_tlw-opacity-65 disabled_tlw-shadow-none'
                        ref={inputRef}
                        value={newSearchName || ''}
                        onKeyDown={onKeyDown}
                        onChange={onNewSearchNameChange}
                    />
                    {changedSearchText && isInvalidSavedSearch &&
                    <div className='tlw-text-sm tlw-text-red-500'>
                        {isInvalidSavedSearch.error}
                    </div>
                    }
                </div>
                <div className='tlw-border-0 tlw-border-b tlw-border-solid tlw-border-gray-300'
                />
                <div
                    className='tlw-m-0 tlw-flex tlw-grow tlw-flex-row tlw-items-center tlw-p-7.5 tlw-text-base tlw-leading-22 tlw-text-gray-900'
                >

                    <RIQButton
                        variant='default'
                        className='tlw-mx-4 tlw-ml-auto tlw-self-center tlw-px-6 tlw-py-3'
                        onClick={onCloseClick}
                    >
                        Close
                    </RIQButton>

                    <RIQButton
                        variant='primary'
                        className='tlw-mx-4 tlw-self-center tlw-px-6 tlw-py-3'
                        onClick={onSaveClick}
                        disabled={Boolean(isInvalidSavedSearch || newSearchName === savedSearch.name)}
                    >
                        Save Change
                    </RIQButton>
                </div>
            </RIQModal>
        </>
    );
}


function shareOfVoiceFormatter(value, abbreviated = false) {
    const formatter = new Intl.NumberFormat('en-us', {
        maximumFractionDigits: (abbreviated ? 0 : 2),
        style: 'percent'
    });
    return formatter.format(value);
}

function SavedSearchTableRow({savedSearch, savedSearchMetricResults, isMetricsLoading, formattersByRequestName, metricRequests, isSampleTopicTable}) {

    const isExporting = useIsExporting();
    const viewSavedSearch = useViewListeningQuery();

    const {setTriggerRef: editTriggerRef, tooltipNode: editTooltipNode} = useRIQTooltip({
        text: 'Edit Search',
        placement: 'top',
        delayShow: 200
    });

    const {setTriggerRef: deleteTriggerRef, tooltipNode: deleteTooltipNode} = useRIQTooltip({
        text: 'Delete Search',
        placement: 'top',
        delayShow: 200
    });

    function onEditSavedSearch(e, configuration) {
        e.stopPropagation();

        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ListeningClickedEdit, {
            source: 'saved-search-list'
        });

        RivalIQ.execute('history:navigate', `listening/savedsearchbuilder/${configuration.id}`, {
            trigger: true
        });
    }

    const keys = [
        'shareOfVoice',
        'count',
        'netSentiment',
        'countLast24Hours',
        'netSentimentLast24Hours'
    ];

    const rowClassNames = 'tlw-w-full tlw-truncate tlw-text-right max-sm_tlw-whitespace-nowrap';
    function sentimentRowClassNames(sentiment) {
        return `tlw-w-full tlw-truncate tlw-text-right ${sentiment < 0 ? 'tlw-text-red-400' : 'tlw-text-green-400'} max-sm_tlw-whitespace-nowrap`;
    }

    // shareOfVoice uses the formatter defined above
    formattersByRequestName.shareOfVoice = {
        abbreviated: function (val) {
            return shareOfVoiceFormatter(val, true);
        },
        expanded: shareOfVoiceFormatter
    };

    return (
        <tr
            key={savedSearch.id}
            className='tlw-h-[47px] tlw-cursor-pointer tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50'
            onClick={() => viewSavedSearch(savedSearch.id)}>

            <td
                className='tlw-min-w-96 tlw-p-4'>
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <div
                        className='tlw-flex-grow tlw-whitespace-nowrap'
                    >
                        {savedSearch.name}
                    </div>
                </div>
            </td>
            {!isMetricsLoading &&
                <>
                    {
                        _.map(keys, function (key) {
                            const metricValue = savedSearchMetricResults?.[key]?.[0] || [];
                            const abbreviatedValue = _.isNumber(metricValue[0]) ? formattersByRequestName[key].abbreviated(metricValue) : '--';
                            const expandedValue = _.isNumber(metricValue[0]) ? formattersByRequestName[key].expanded(metricValue) : '--';
                            const sparklineData = savedSearchMetricResults?.[`${key}ByDay`];
                            return (
                                <MetricTableCell
                                    key={key}
                                    className={key.includes('netSentiment') ? sentimentRowClassNames(metricValue) : rowClassNames}
                                    abbreviatedValue={abbreviatedValue}
                                    metricName={RivalIQ.metrics.new.MetricRegistry.getName(metricRequests[key], true)}
                                    expandedValue={expandedValue}
                                    alignment={MetricTableCell.Alignment.RIGHT}
                                    sparklineData={sparklineData}
                                />
                            );
                        })
                    }
                </>
            }

            {isMetricsLoading &&
                _.map(keys, (requestName) => {
                    return (
                        <td
                            key={`spinner-${requestName}`}
                            className='tlw-p-4'
                        >
                            <div className='tlw-text-right tlw-text-gray-500'>
                                <span className='fa fa-circle-o-notch fa-spin'></span>
                            </div>
                        </td>
                    );
                })
            }
            {!isExporting &&
                <td
                    className='tlw-w-[84px] tlw-text-right'
                >
                    <div
                        className='tlw-flex tlw-h-full tlw-flex-row tlw-justify-end'
                    >
                        {!isSampleTopicTable &&
                            <RenameTopicButton
                                savedSearch={savedSearch}
                            />
                        }

                        <button
                            className='tlw-border-none tlw-bg-transparent tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal tlw-shadow-none'
                            onClick={(e) => onEditSavedSearch(e, savedSearch)}
                            ref={editTriggerRef}
                        >
                            <span className='fa fa-fw fa-wrench tlw-text-md' />
                        </button>
                        {editTooltipNode}

                        {!isSampleTopicTable && <DeleteListeningQueryButton
                            listeningQuery={savedSearch}
                            ref={deleteTriggerRef}
                        />}
                        {deleteTooltipNode}
                    </div>
                </td>
            }
        </tr>
    );
}

function useSavedSearchMetricRequests(isSampleTopicTable, sortMetricName, sortOrder, isAccountLevel, asObject = false) {
    const landscapeId = useCurationConfigValue('landscapeId');

    const timezone = useCurationConfigValue('timezone');

    const {dateRanges} = useListeningSearchTimePeriod(isSampleTopicTable, false);

    const timePeriodMainPeriodStart = dateRanges.main.start;
    const timePeriodMainPeriodEnd = dateRanges.main.end;

    const baseDate = moment.utc().startOf('hour').subtract(1, 'hour');
    const mainPeriodStartDate = baseDate.clone().subtract(24, 'hours').toISOString();
    const mainPeriodEndDate = baseDate.toISOString();

    const listeningFilters = {
        listeningQueryTypes: [
            RivalIQ.enums.listeningQueryTypes.SavedSearch.id
        ]
    };
    if (isSampleTopicTable) {
        listeningFilters.sampleSavedSearches = true;
    } else if (isAccountLevel) {
        listeningFilters.accountSavedSearches = true;
    } else {
        listeningFilters.landscapeId = landscapeId;
    }

    const metricRequests = [
        ['name', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: {
                field: 'name'
            },
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['shareOfVoice', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: {
                field: 'count',
                transform: 'percentOfTotal'
            },
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['count', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'count',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['countByDay', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            measure: 'count',
            dimensions: ['listeningQueryId', 'day'],
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['netSentiment', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['netSentimentByDay', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId', 'day'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        }],
        ['countLast24Hours', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'count',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: mainPeriodStartDate,
                    endDate: mainPeriodEndDate
                }
            }
        }],
        ['netSentimentLast24Hours', {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: mainPeriodStartDate,
                    endDate: mainPeriodEndDate
                }
            }
        }]
    ];

    if (sortMetricName) {
        const sortMetricDefinition = _.find(metricRequests, ([name]) => name === sortMetricName);
        if (sortMetricDefinition) {
            const ordering = _.cloneDeep(_.last(sortMetricDefinition));
            delete ordering.filters?.ordering;
            ordering.sortOrder = sortOrder;

            _.each(metricRequests, ([name, request]) => {
                if (request.dimensions.length > 1) {
                    return;
                }

                request.filters.ordering = {
                    sort: [ordering]
                };
            });
        }
    }

    if (!asObject) {
        return metricRequests;
    }

    return _.reduce(metricRequests, (memo, [name, conf]) => _.set(memo, name, conf), {});
}

function SavedSearchTable({allSavedSearches, emptyFallback, instanceId, isLoadingAllSavedSearches, isSampleTopicTable, isAccountLevel}) {
    const isExporting = useIsExporting();
    const statusUpdater = useSetInstanceLoadingStatus(instanceId);

    const [sortMetricName, setSortMetricName] = useCurationConfigState(instanceId, 'sortMetricName');
    const [sortOrder, setSortOrder] = useCurationConfigState(instanceId, 'sortOrder');

    const metricRequests = _.omit(useSavedSearchMetricRequests(isSampleTopicTable, sortMetricName, sortOrder, isAccountLevel, true), ['name']);

    function determineSortIndicator(metricName) {
        if (metricName === sortMetricName) {
            return sortOrder === 'desc' ? MetricTableHeader.SortIndicator.SORT_DOWN : MetricTableHeader.SortIndicator.SORT_UP;
        }
    }

    function onHeaderClick(metricName) {
        if (metricName === sortMetricName) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortMetricName(metricName);
            setSortOrder('asc');
        }
    }

    const {data: metricResults, isLoading: isMetricsLoading} = useMetrics(metricRequests);

    const formattersByRequestName = metricResults ? _.mapValues(metricRequests, (request, requestName) => {
        const metricResult = metricResults[requestName];
        return {
            abbreviated: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expanded: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded)
        };
    }) : {};

    // NOTE we do this monkey business so that exports wait until the metrics
    // have finished loading before capture, thereby preventing accidental
    // capture during loading states
    useEffect(() => {
        statusUpdater(s => LoadingStatus[(isMetricsLoading ? 'LOADING' : 'COMPLETE')]);
    }, [isMetricsLoading, statusUpdater]);

    const resultsByListeningQueryId = _.reduce(metricResults, (memo, results, requestName) => {

        _.each(results.data, result => {
            const listeningQueryId = result[0]; // first dimension is always listeningQueryId in the metric requests in metricRequests.

            if (!memo[listeningQueryId]) {
                memo[listeningQueryId] = {};
            }

            if (!memo[listeningQueryId][requestName]) {
                memo[listeningQueryId][requestName] = [];
            }
            memo[listeningQueryId][requestName].push(_.drop(result, 1));
        });

        return memo;
    }, {});

    if (isLoadingAllSavedSearches) {
        return (
            <div className='tlw-grid tlw-h-[100px] tlw-place-items-center'>
                <div className='tlw-text-[36px] tlw-text-gray-500'>
                    <span className='fa fa-circle-o-notch fa-spin'></span>
                </div>
            </div>
        );
    }

    let tableBody;
    if (emptyFallback && _.isEmpty(allSavedSearches)) {
        tableBody = emptyFallback;
    } else {

        const orderedListeningQueryIds = _.map(metricResults?.shareOfVoice?.data, _.first);
        const orderedSavedSearches = _.sortBy(allSavedSearches, savedSearch => _.indexOf(orderedListeningQueryIds, savedSearch.id));
        tableBody = _.map(orderedSavedSearches, savedSearch => {
            const savedSearchMetricResults = resultsByListeningQueryId[savedSearch.id];
            return (
                <SavedSearchTableRow
                    key={savedSearch.id}
                    savedSearch={savedSearch}
                    savedSearchMetricResults={savedSearchMetricResults}
                    isMetricsLoading={isMetricsLoading}
                    formattersByRequestName={formattersByRequestName}
                    metricRequests={metricRequests}
                    isSampleTopicTable={isSampleTopicTable}
                />
            );
        });
    }

    return (
        <div className='tlw-overflow-x-auto max-sm_tlw-w-full max-sm_tlw-overflow-y-hidden'>
            <table className='tlw-w-full'>
                <thead>
                    <tr className='tlw-bg-gray-150'>
                        <MetricTableHeader
                            title='Search Name'
                            className='max-[767px]_tlw-whitespace-nowrap'
                        />
                        <MetricTableHeader
                            title='Share of Voice'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.shareOfVoice}
                            sortIndicator={determineSortIndicator('shareOfVoice')}
                            onClick={() => onHeaderClick('shareOfVoice')}
                        />
                        <MetricTableHeader
                            title='Posts'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.count}
                            sortIndicator={determineSortIndicator('count')}
                            onClick={() => onHeaderClick('count')}
                        />
                        <MetricTableHeader
                            title='Net Sentiment'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.netSentiment}
                            sortIndicator={determineSortIndicator('netSentiment')}
                            onClick={() => onHeaderClick('netSentiment')}
                        />
                        <MetricTableHeader
                            title={<MetricTimePeriodHeaderTitle
                                metric='Posts'
                                timePeriod='Last 24 Hours'/>}
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.countLast24Hours}
                            sortIndicator={determineSortIndicator('countLast24Hours')}
                            onClick={() => onHeaderClick('countLast24Hours')}
                        />
                        <MetricTableHeader
                            title={<MetricTimePeriodHeaderTitle
                                metric='Net Sentiment'
                                timePeriod='Last 24 Hours'/>}
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.netSentimentLast24Hours}
                            sortIndicator={determineSortIndicator('netSentimentLast24Hours')}
                            onClick={() => onHeaderClick('netSentimentLast24Hours')}
                        />
                        {!isExporting &&
                            <th
                                className='tlw-w-30'
                            />
                        }
                    </tr>
                </thead>
                <tbody>{tableBody}</tbody>
            </table>
        </div>
    );
}

function SavedSearchListFooterContext({savedSearches, limit}) {
    if (!savedSearches) {
        return null;
    }

    return (
        <span className='tlw-self-center tlw-text-sm'>You have used <strong>{_.size(savedSearches)}</strong> of <strong>{limit}</strong> saved searches your plan allows. <RIQInfoPopover description={description}/></span>
    );
}

function onContactUsClick(e) {
    e.preventDefault();
    if (window.Intercom) {
        window.Intercom('show');
    }
}

export function SavedSearchTableEmpty({isAtLimit}) {
    let message = `Create Saved Searches to revisit queries, receive alerts on post volume spikes, and easily compare the volume and sentiment of multiple searches.`;
    if (isAtLimit) {
        message = (
            <>
                <button
                    className='tlw-border-none tlw-bg-transparent tlw-p-0 tlw-text-blue-400 hover_tlw-text-blue-450 hover_tlw-underline focus_tlw-text-blue-450 focus_tlw-underline'
                    onClick={onContactUsClick}>Contact us</button> to add additional saved searches to your account, so you can easily revisit the search, receive alerts on post volume spikes, and easily compare the volume and sentiment of multiple searches.
            </>
        );
    }

    return (
        <tr>
            <td
                className='tlw-p-10 tlw-text-center'
                colSpan='7'>
                <h3 className='tlw-text-lg'>This landscape doesn&rsquo;t have any saved searches.</h3>
                <p className='tlw-text-sm'>{message}</p>
            </td>
        </tr>
    );
}

function hasInterval(metric) {
    const dimensions = _.get(metric, 'dimensions', []);
    return _.some(RivalIQ.enums.timePeriod.interval, (interval) => dimensions.indexOf(interval) >= 0);
}

export function SavedSearchListWidget({appSpecificExportConfig, pdfExportConfig, instanceId}) {

    const sortMetricName = useCurationConfigValue(instanceId, 'sortMetricName');
    const sortOrder = useCurationConfigValue(instanceId, 'sortOrder');

    const metricRequests = useSavedSearchMetricRequests(false, sortMetricName, sortOrder, false, false);

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        appSpecificExportConfig,
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            headerNameOverrides: [
                'search_name',
                'share_of_voice',
                'posts',
                'net_sentiment',
                'posts_last_24_hours',
                'net_sentiment_last_24_hours'
            ],
            metricGroups: [
                _.reduce(metricRequests, (memo, [name, conf]) => (hasInterval(conf) ? memo : memo.concat(conf)), [])
            ]
        },
        pdfExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const isExporting = useIsExporting();
    const plan = useCurationConfigValue('plan');
    const planSavedSearchesLimit = plan?.max_listening_saved_searches;

    const {data: accountSavedSearches, isLoading: isLoadingAccountSavedSearches} = useListAccountSavedSearches();
    const atLimit = (_.size(accountSavedSearches) >= planSavedSearchesLimit);

    const {data: allSavedSearches, isLoading: isLoadingAllSavedSearches} = useListLandscapeSavedSearches();

    if (!plan?.has_full_instant_search || plan?.max_listening_saved_searches === 0) {
        return;
    }

    let footerContextView = null;
    if (!isExporting) {
        footerContextView = <SavedSearchListFooterContext
            limit={planSavedSearchesLimit}
            savedSearches={(isLoadingAccountSavedSearches ? null : accountSavedSearches)}/>;
    }

    return (
        <Panel
            footerContextView={footerContextView}
            instanceId={instanceId}
            showViewMore={false}
            title={title}
        >
            <SavedSearchTable
                allSavedSearches={allSavedSearches}
                emptyFallback={<SavedSearchTableEmpty isAtLimit={atLimit}/>}
                instanceId={instanceId}
                isLoadingAllSavedSearches={isLoadingAllSavedSearches}
            />
        </Panel>
    );
}

export function SampleTopicsListWidget({instanceId}) {

    const {
        title
    } = listeningDefinitions[instanceId] || {};

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title
        }
    });

    const {data: allSavedSearches, isLoading: isLoadingAllSavedSearches} = useListSampleListeningQueries();

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            showViewMore={false}
        >
            <SavedSearchTable
                allSavedSearches={allSavedSearches}
                isLoadingAllSavedSearches={isLoadingAllSavedSearches}
                instanceId={instanceId}
                isSampleTopicTable={true}
            />
        </Panel>
    );
}
